import React, { useEffect, useState } from 'react';
import { Loader2, IndianRupee, TrendingUp, Users, Building2, RotateCcw, BadgeCheck, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { api, formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const Kpi = ({ label, value, accent = 'gray', icon: Icon, hint, testId }) => (
  <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
        {Icon && <Icon className={`h-4 w-4 text-${accent}-500`} />}
      </div>
      <p className={`mt-1 text-2xl font-bold text-${accent}-700 dark:text-${accent}-300`}>{value}</p>
      {hint && <p className="text-[10px] text-gray-500 mt-1">{hint}</p>}
    </CardContent>
  </Card>
);

const Bar = ({ rows, valueKey, labelKey = 'month', secondaryKey, valueFormatter = fmtINR, color = 'red' }) => {
  const max = Math.max(...rows.map((r) => Number(r[valueKey] || 0)), 1);
  return (
    <div className="flex items-end gap-2 h-40 overflow-x-auto pb-1">
      {rows.map((r, i) => {
        const h = Math.max(6, Math.round((Number(r[valueKey] || 0) / max) * 100));
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-[36px]">
            <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 whitespace-nowrap">
              {valueFormatter(r[valueKey])}
            </span>
            <div className={`w-full bg-gradient-to-t from-${color}-600 to-${color}-400 rounded-t`} style={{ height: `${h}%` }} title={secondaryKey ? `${secondaryKey}: ${r[secondaryKey]}` : ''} />
            <span className="text-[9px] text-gray-500 whitespace-nowrap">{r[labelKey]}</span>
          </div>
        );
      })}
    </div>
  );
};

const AdminCrmAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/crm-analytics/overview', { params: { days: d } });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading || !data) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>;
  }

  const { sales, customers, daily, monthly, top_destinations: td, suppliers, booking_mix: mix } = data;

  return (
    <div className="space-y-6" data-testid="crm-analytics-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">CRM Analytics</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Daily · Monthly · Customer · Supplier overview</p>
        </div>
        <div className="flex gap-1.5">
          {[30, 90, 180, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    className={`px-3 py-1.5 rounded text-xs font-semibold transition ${days === d ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200'}`}
                    data-testid={`window-${d}d`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Sales KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi testId="sales-bookings" label="Total Bookings"      value={sales.total_bookings} icon={BadgeCheck} accent="gray" />
        <Kpi testId="sales-gbv"      label="Gross Booking Value" value={fmtINR(sales.gross_booking_value)} icon={IndianRupee} accent="red" />
        <Kpi testId="sales-revenue"  label="Revenue (Paid)"      value={fmtINR(sales.revenue_paid)} icon={TrendingUp} accent="emerald" />
        <Kpi testId="sales-profit"   label="Net Profit"          value={fmtINR(sales.net_profit)} icon={BadgeCheck} accent="blue" />
        <Kpi testId="sales-refunds"  label="Refunds"             value={fmtINR(sales.refunds)} icon={RotateCcw} accent="amber" />
        <Kpi testId="sales-out"      label="Outstanding"         value={fmtINR(sales.outstanding)} icon={AlertCircle} accent="amber" />
        <Kpi testId="sales-cancel"   label="Cancellations"       value={sales.cancellations} icon={RotateCcw} accent="amber" />
      </div>

      {/* Daily revenue bar */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-4">
          <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">Daily Revenue (last 30 days)</p>
          {daily.length === 0 ? <p className="text-xs text-gray-500">No paid bookings in this period.</p>
            : <Bar rows={daily} valueKey="revenue" labelKey="date" secondaryKey="bookings" color="red" />}
        </CardContent>
      </Card>

      {/* Monthly revenue + profit */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-4">
          <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">Monthly Revenue (last 12 months)</p>
          {monthly.length === 0 ? <p className="text-xs text-gray-500">No monthly data yet.</p>
            : <Bar rows={monthly} valueKey="revenue" labelKey="month" secondaryKey="profit" color="emerald" />}
        </CardContent>
      </Card>

      {/* Customer metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Kpi testId="cust-distinct" label="Distinct Customers" value={customers.total_distinct} icon={Users} accent="blue" />
        <Kpi testId="cust-new"      label="New (last 30d)"     value={customers.new_in_30d} icon={Users} accent="emerald" />
        <Kpi testId="cust-repeat"   label="Repeat Customers"   value={customers.repeat} icon={Users} accent="blue" />
        <Kpi testId="cust-retention" label="Retention %"       value={`${customers.retention_pct}%`} icon={TrendingUp} accent="emerald" />
        <Kpi testId="cust-corp"     label="Corporate Accounts" value={customers.corporate_accounts} icon={Building2} accent="blue" />
      </div>

      {/* Booking type mix */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-4">
          <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">Booking Type Mix</p>
          {mix.length === 0 ? <p className="text-xs text-gray-500">No data</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {mix.map((m) => (
                <div key={m.booking_type} className="border border-gray-200 dark:border-gray-800 rounded-lg p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 capitalize">{m.booking_type}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{m.bookings}</p>
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-300">{fmtINR(m.revenue)}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top destinations */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-4">
          <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">Top Destinations</p>
          {td.length === 0 ? <p className="text-xs text-gray-500">No data</p> : (
            <ul className="space-y-1.5">
              {td.map((d, i) => (
                <li key={i} className="flex items-center justify-between text-sm border-b border-gray-100 dark:border-gray-800 pb-1">
                  <span className="font-medium">{d.destination}</span>
                  <span className="flex items-center gap-3 text-xs">
                    <span className="text-gray-500">{d.bookings} bookings</span>
                    <span className="font-bold text-emerald-600 dark:text-emerald-300">{fmtINR(d.revenue)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Supplier breakdown */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <p className="text-sm font-bold text-gray-900 dark:text-white">Supplier Breakdown</p>
          </div>
          {suppliers.length === 0 ? <div className="text-center py-8 text-gray-500">No supplier data</div> : (
            <table className="w-full text-sm" data-testid="supplier-breakdown-table">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Supplier</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Bookings</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Revenue</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Profit</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Cancellations</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Cancel %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {suppliers.map((s) => (
                  <tr key={s.name}>
                    <td className="px-4 py-2 text-xs font-semibold text-gray-900 dark:text-white">{s.name}</td>
                    <td className="px-4 py-2 text-xs text-right">{s.bookings}</td>
                    <td className="px-4 py-2 text-xs text-right text-emerald-700 dark:text-emerald-300 font-bold">{fmtINR(s.revenue)}</td>
                    <td className="px-4 py-2 text-xs text-right text-blue-700 dark:text-blue-300 font-bold">{fmtINR(s.profit)}</td>
                    <td className="px-4 py-2 text-xs text-right">{s.cancellations}</td>
                    <td className="px-4 py-2 text-xs text-right">
                      <Badge className={s.cancellation_rate_pct > 20 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                        {s.cancellation_rate_pct}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCrmAnalytics;
